// START IMPORTS
// Context and provider imports
import { LLMProvider, ReportContextProvider } from '@ltvco/refresh-lib/ctx';
// Hooks imports
import { useReport } from '@ltvco/refresh-lib/hooks';
// Theme and UI imports
import { Grid, Box } from '@ltvco/refresh-lib/theme';
// Utility imports
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  DateUtil,
  calculateMarketPricesRange,
  useScreenSize,
} from '@ltvco/refresh-lib/utils';
// Core lib v1 imports
import {
  DebugMenu,
  MonitoringBanner,
  peopleTypes,
  PerksBannerSection,
  PropertyReport,
  PropertyResults,
  PropertySmartInsightsContainer,
  ReportChangesOverview,
  ReportLoading,
  SerializedListingsData,
  useRemouladeReportSnapshot,
  useReportMonitors,
  useSearchListings,
  useSession,
  useTuxedo,
} from '@ltvco/refresh-lib/v1';
// Core lib v2 imports
import {
  LizanoListItemDataProperty,
  ReportActionsWithDateUpdated,
  PdfSection,
  ReportNullState,
  ReportNavigationMenu,
  ReportNavigationMobileMenu,
  PropertyReportOverview,
  PropertyOwnersSection,
  ResidentsSection,
  BuildingPermits as BuildingPermitsV2,
  HomeOwnerAssociationSection,
  PropertyLiensSection,
  PropertySchools,
  NaturalHazards,
  DeedsSection,
  AssessedValue,
  PropertyValuationSection,
  PropertyDemographicsSection,
  PropertyLotBuildingSection,
  EditPropertyDetailsModal,
  ReportRating,
  NotesSection,
  RelatedReportsSection,
  MarketInsightsSection,
  buildPreForeclosureTab,
  buildRecentlySoldHomeTab,
  type ReportNavigationMenuProps,
  CrimeFeedCTA,
} from '@ltvco/refresh-lib/v2';
// Vendor imports
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import Edit from '@mui/icons-material/Edit';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// App-specific imports
import {
  getPropertyNavLinkData,
  getPropertyNavLinkDataV2,
} from 'navLinkData/propertyNavLinkData';
import {
  groupAvmsBySource,
  getPropertyDetailsCount,
  getPropertyValuesCount,
} from 'utils/propertyReportUtils';
import { useQueryParams } from 'utils/useQueryParams';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface PropertyReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

export function PropertyReportPage({ permalink }: PropertyReportProps) {
  // START HOOKS_AND_QUERIES
  // TODO: all this monitored report logic needs a refactor.
  // There is no point in refetching a report when a user toggles monitoring, so we use the initial value.
  const { reportIsMonitored } = useReportMonitors();
  const coreResult = useReport(permalink);
  const remouladeResult = useRemouladeReportSnapshot(permalink);
  const flattenedRemouladeResult = remouladeResult
    ? { ...remouladeResult, data: remouladeResult.data?.data }
    : remouladeResult;

  const shouldUseRemouladeSnapshot =
    !remouladeResult.isError && reportIsMonitored;

  const navigate = useNavigate();
  const isInTaxHistoryVariation = useFeatureIsOn('prop-1877');
  const isInPropertySmartInsightsVariation = useFeatureIsOn('prop-2296');
  const isInListV2Variation = useFeatureIsOn('prop-2834');

  const queryReportResult = (
    shouldUseRemouladeSnapshot ? flattenedRemouladeResult : coreResult
  ) as typeof coreResult;

  const zeroedReport = isZeroed(queryReportResult as any);

  const queryParams = useQueryParams();
  const { isMobile } = useScreenSize();

  const { hash } = useLocation();
  useScrollToSectionOnNavigate(hash, queryReportResult as any);

  const {
    userProfile,
    homeDataSet,
    homeAdditions,
    setupUserProfile,
    createUserProfile,
  } = useTuxedo();
  const { session } = useSession();
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [temporaryPermalink, setTemporaryPermalink] = useState<string | null>(
    null
  );
  const [hasTriggeredMutation, setHasTriggeredMutation] = useState(false);
  const [userProfileTuxedo, setUserProfileTuxedo] = useState(null);
  const [readyToFetch, setReadyToFetch] = useState(false);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const fullAddress = generateFullAddressFromParams(queryParams);
  const firstProperty = queryReportResult?.data?.entities?.properties?.[0];
  const postHomeData = homeDataSet;
  const tuxedoBuilding = homeAdditions(temporaryPermalink) || { data: null };

  const searchData = {
    // 'byram' is the name of the DD Plan which includes Listhub data
    // to the listings endpoints response (WC - 2023/10/05).
    dd_plan: 'byram',
    rentals_limit: '10',
    pfc_limit: '10',
    listings_limit: '10',
    city,
    state,
    brand: 'nw',
  };

  const { data: searchListingsResult = [] } = useSearchListings(
    searchData,
    Boolean(city && state)
  );
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    setCity(firstProperty?.parcel_address?.parsed?.city || '');
    setState(firstProperty?.state || '');
  }, [firstProperty?.parcel_address?.parsed?.city, firstProperty?.state]);

  useEffect(() => {
    if (!firstProperty || hasTriggeredMutation) {
      return;
    }

    if (!readyToFetch) {
      setReadyToFetch(true);
    }
  }, [firstProperty, hasTriggeredMutation]);

  // Handling the user profile creation and query success
  useEffect(() => {
    if (readyToFetch) {
      if (userProfile.isError && !hasTriggeredMutation) {
        if (
          !userProfile?.data?.user_profile &&
          session?.account?.account?.user_info &&
          firstProperty
        ) {
          const userProfileData = setupUserProfile(
            session.account.account.user_info,
            firstProperty.mailing_address
          );

          userProfileData.record_search = {
            address: firstProperty?.parcel_address?.full || '',
            city: firstProperty?.parcel_address?.parsed?.city || '',
            state: firstProperty?.parcel_address?.parsed?.state || '',
            zip_code: firstProperty?.parcel_address?.parsed?.zip5 || '',
          };

          createUserProfile.mutate({
            userProfile: {
              user_profile: userProfileData,
              record_search: userProfileData.record_search,
            },
          });

          setHasTriggeredMutation(true);
        }
      }

      if (userProfile.isSuccess && userProfile.data?.user_profile) {
        setTemporaryPermalink(permalink);
        setUserProfileTuxedo(userProfile.data.user_profile);
      }
    }
  }, [readyToFetch, userProfile, session, firstProperty, createUserProfile]);

  // Update state when userProfile fetch succeeds
  useEffect(() => {
    if (userProfile.isSuccess && userProfile.data?.user_profile) {
      setTemporaryPermalink(permalink);
      setUserProfileTuxedo(userProfile.data.user_profile);
    }
  }, [userProfile, permalink]);
  // END EFFECTS

  // START EARLY_RETURNS
  // Handling main report fetched
  if (queryReportResult.isLoading || queryReportResult.isError) {
    return <ReportLoading menuItems={10} />;
  }

  if (zeroedReport) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION

  const handleOpenEditModal = () => {
    setIsEditModalOpen(!isEditModalOpen);
  };

  const report = new PropertyReport(queryReportResult?.data);

  if (!report) {
    return <ReportLoading menuItems={10} />;
  }

  const property = report.data.properties[0];
  const responseHasPropertyDataAndNoEntitiesFlagIsNull =
    (report.data?.meta?.counts?.properties ?? 0) > 0 &&
    queryReportResult?.data?.meta?.no_entities === null;
  const noEntitiesFlagIsEitherTrueOrNull =
    queryReportResult?.data?.meta?.no_entities ||
    queryReportResult?.data?.meta?.no_entities === null;

  if (
    !responseHasPropertyDataAndNoEntitiesFlagIsNull &&
    noEntitiesFlagIsEitherTrueOrNull
  ) {
    const options = queryReportResult?.data?.entities?.options;
    const fromParam = queryParams.get('from') || '';
    return (
      <ReportContextProvider permalink={permalink} reportType="property">
        <Grid
          sx={{
            bgcolor: 'background.default',
            flexDirection: 'column',
            paddingBottom: '20px',
            px: 2,
          }}
          container
        >
          <PropertyResults
            searchAddress={report?.data?.address || fullAddress}
            options={options || { businesses: [], properties: [], people: [] }}
            fromDashboard={fromParam}
            pageOptions={{
              disableSubheadline: Boolean(
                options?.properties?.length && options.properties.length < 1
              ),
            }}
          />
        </Grid>
      </ReportContextProvider>
    );
  }

  const userProfileForEditModal = userProfileTuxedo
    ? userProfileTuxedo
    : setupUserProfile(
        session.account?.account?.user_info,
        property?.mailing_address
      );
  let updatedBuilding = property?.buildings?.[0];

  if (
    userProfileTuxedo &&
    tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
  ) {
    const totalRoomsValue =
      tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
        ?.custom_attributes?.beds +
      tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
        ?.custom_attributes?.baths;

    const updatedBuildingTuxedo = {
      ...updatedBuilding,
      rooms: {
        ...updatedBuilding?.rooms,
        bed: tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
          ?.custom_attributes?.beds,
        baths: {
          ...updatedBuilding?.rooms?.baths,
          total:
            tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
              ?.custom_attributes?.baths,
        },
        total: totalRoomsValue,
      },
      area: tuxedoBuilding?.data?.user_data_sets?.user_property_detail_data_set
        ?.custom_attributes?.sqft,
    };
    updatedBuilding = updatedBuildingTuxedo;
  }

  const displayAddress = report.data.address || fullAddress;
  const marketValues = property.market;
  const marketSortedBySource = groupAvmsBySource(marketValues);
  const propertyDetailsCount = getPropertyDetailsCount(property);
  const PropertyValuesCount = getPropertyValuesCount(property);
  const propertyNavLinkData = getPropertyNavLinkData(
    property?.owners.length,
    property?.residents.length,
    property?.building_permits ? property?.building_permits.length : 0,
    property?.deeds.length,
    property?.courts.civils.length,
    propertyDetailsCount || 0,
    PropertyValuesCount || 0,
    report?.data?.properties?.[0]?.equities?.length || 0,
    report?.data?.schools?.length || 0
  );

  // Property Report Navigation Menu Data
  const propertyNavLinkDataV2 = getPropertyNavLinkDataV2(
    report.data,
    marketSortedBySource
  ) as ReportNavigationMenuProps['menuItems'];

  // MarketInsights data
  const listingsFetchResults = (searchListingsResult as SerializedListingsData)
    ?.listing_teaser;
  const preforeclosureData = listingsFetchResults?.preforeclosures || [];
  const totalPreforeclosures =
    listingsFetchResults?.meta?.available?.market?.preforeclosures || 0;

  const listingsNavigateAction = (property: any, type?: string) => {
    const { city, state, zip5 } = property?.parcel_address?.parsed || {
      city: '',
      state: '',
      zip5: '',
    };
    const url = `/search/listing?city=${city}&state=${state}&zip5=${zip5}&search_type=${
      type || 'pre_foreclosures'
    }`;
    navigate(url);
  };

  const marketInsightsTabs = [
    buildRecentlySoldHomeTab({
      property,
      data: property.comparables,
      isMobile,
    }),
    buildPreForeclosureTab({
      property,
      data: preforeclosureData,
      isMobile,
      navigateAction: listingsNavigateAction,
      totalDataValues: totalPreforeclosures,
    }),
  ];

  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';
  document.title = `${displayAddress} - BeenVerified`;

  const buildReportDataForListV2 = () => {
    const data: LizanoListItemDataProperty = {
      permalink,
      full_address: report.data.address || fullAddress,
      street: property?.parcel_address?.parts?.street || '',
      city: property?.parcel_address?.parts?.city || '',
      state: property?.parcel_address?.parts?.state || '',
      zip5: property?.parcel_address?.parts?.zip || '',
    };

    if (property?.buildings?.[0]?.rooms?.bed) {
      data.beds = property?.buildings?.[0]?.rooms?.bed;
    }

    if (property?.buildings?.[0]?.rooms?.baths?.total) {
      data.baths = property?.buildings?.[0]?.rooms?.baths?.total;
    }

    if (property?.buildings?.[0]?.area) {
      data.sqft = property?.buildings?.[0]?.area;
    }

    if (marketValues && marketValues.length) {
      const AVM_PARTNERS_ARRAY = ['bk', 'ca', 'arch'];

      const marketRange = calculateMarketPricesRange(
        marketValues,
        AVM_PARTNERS_ARRAY
      );

      data.estimated_value = marketRange?.maxRange || 0;
    }

    if (property.owners && property.owners.length) {
      data.owners = (property.owners as peopleTypes.Identities).map(
        (owner: peopleTypes.Identity) => {
          return {
            first_name: owner.names?.[0]?.parsed?.first || '',
            last_name: owner.names?.[0]?.parsed?.last || '',
          };
        }
      );
    }

    return data;
  };

  const extraMenuActionItems = [
    {
      display: 'Edit Building Details',
      icon: <Edit fontSize="small" color="primary" />,
      action: () => handleOpenEditModal(),
    },
  ];
  // END DATA_PREPARATION

  // START RENDER
  return (
    <LLMProvider llmData={property} llmType="property">
      <Grid container direction={'row'} columns={12} spacing={7}>
        {/* START DESKTOP_NAVIGATION */}
        <Grid
          size={{ sm: 12, md: 4, lg: 4 }}
          sx={{ display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' } }}
        >
          <ReportNavigationMenu
            reportType="Property Report"
            headerTitle={displayAddress}
            menuItems={propertyNavLinkDataV2}
            customTitleStyle={{
              fontSize: '1.125rem',
              fontWeight: 700,
              letterSpacing: 0,
              color: 'text.primary',
              textOverflow: 'unset',
              wordBreak: 'break-word',
            }}
          />
        </Grid>
        <Grid
          size={{ sm: 12, md: 8, lg: 8 }}
          marginTop={0.5}
          sx={{
            '&.MuiGrid-item': {
              paddingTop: { xs: 3.75, md: 7 },
            },
          }}
        >
          {reportIsMonitored && (
            <ReportChangesOverview
              permalink={permalink}
              navLinkData={propertyNavLinkData}
            />
          )}

          <>
            <Box
              sx={{
                height: 30,
                backgroundColor: '#f8f8f8',
                position: 'sticky',
                marginBottom: '-10px',
                marginX: -1,
                top: 52,
                zIndex: 10,
                display: {
                  xs: 'none',
                  sm: 'block',
                  md: 'block',
                  lg: 'block',
                },
              }}
            />

            <ReportActionsWithDateUpdated
              reportType={'property'}
              reportTitle="Property"
              dateUpdated={reportUpdateDate}
              reportUpgraded={false}
              reportDataForListV2={
                isInListV2Variation ? buildReportDataForListV2() : undefined
              }
              extraMenuActionItems={extraMenuActionItems}
            />
          </>
          <PropertyReportOverview
            propertyReportData={property}
            serializedPropertyReportData={report.data}
            permalink={permalink}
            updatedBuilding={updatedBuilding}
            marketValues={marketValues}
            allowClaiming={false}
          />
          {/* disabling this for now as tophap widgets are not working */}
          {/* <PropertyNeighborhoodData propertyReportData={property} /> */}
          <CrimeFeedCTA parcelAddress={property.parcel_address || null} />
          <MonitoringBanner
            propertyReportData={property}
            permalink={permalink}
            reportType="property"
          />
          {isInPropertySmartInsightsVariation && (
            <PropertySmartInsightsContainer reportData={report.data} />
          )}
          <PropertyOwnersSection
            owners={property.owners as peopleTypes.Identities}
            residents={property.residents}
            deeds={property.deeds}
            propertyAddress={displayAddress}
            permalink={permalink}
          />
          <ResidentsSection
            residents={property.residents}
            propertyAddress={displayAddress}
            permalink={permalink}
          />
          <PropertyLotBuildingSection
            propertyAddress={displayAddress}
            property={property}
            updatedBuilding={updatedBuilding}
            canEditProperty={true}
            setIsEditModalOpen={handleOpenEditModal}
          />
          <BuildingPermitsV2
            propertyAddress={displayAddress}
            property={property}
            permalink={permalink}
          />
          <HomeOwnerAssociationSection hoas={property.hoas} />
          <AssessedValue
            property={property}
            useTaxHistory={isInTaxHistoryVariation}
          />
          <PropertyValuationSection
            property={property}
            propertyAddress={displayAddress}
            marketValues={marketValues}
            rentalValues={property.values?.rental || []}
            avmsPartners={marketSortedBySource}
            permalink={permalink}
          />
          <DeedsSection deeds={property.deeds} />
          <PropertyLiensSection
            fullAddress={displayAddress}
            liens={property.courts}
            permalink={permalink}
          />
          <MarketInsightsSection
            tabs={marketInsightsTabs.filter((item) => !!item)}
          />
          <PropertyDemographicsSection
            property={property}
            propertyAddress={displayAddress}
          />
          <PropertySchools
            propertyAddress={displayAddress}
            property={report.data}
          />
          {/* <HomesNearby />
          <SurroundingArea /> */}
          <NaturalHazards property={property} />
          <PerksBannerSection permalink={permalink} />
          <PdfSection permalink={permalink} />
          <NotesSection permalink={permalink} />
          <RelatedReportsSection
            reportType="property"
            personName={displayAddress}
            potentialOwners={property.owners}
            relatives={[]}
          />
          <ReportRating
            rating={report.data.meta?.rating || null}
            report_type="property"
          />
          {/* <Comments /> */}
          <EditPropertyDetailsModal
            open={isEditModalOpen}
            setIsOpen={handleOpenEditModal}
            building={updatedBuilding}
            permalink={permalink}
            userProfile={userProfileForEditModal}
            postHomeData={postHomeData}
            propertyAddress={displayAddress}
          />
        </Grid>
        {/* END MAIN_CONTENT */}
      </Grid>

      {/* START MOBILE_NAVIGATION */}
      <ReportNavigationMobileMenu
        reportType="Property Report"
        headerTitle={displayAddress}
        menuItems={propertyNavLinkDataV2}
      />
      {/* END MOBILE_NAVIGATION */}

      {/* START DEBUG_MENU */}
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      {/* END DEBUG_MENU */}
    </LLMProvider>
  );
  // END RENDER
}

// START HELPER_FUNCTIONS
function generateFullAddressFromParams(queryParams: URLSearchParams): string {
  const address = queryParams.get('address') || '';
  const city = queryParams.get('city') || '';
  const state = queryParams.get('state') || '';
  const zipcode = queryParams.get('zipcode') || '';

  return `${address} ${city}, ${state} ${zipcode}`;
}
// END HELPER_FUNCTIONS
